import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;
const SCROLL_POINTER_GUARD_DELAY = 250;
const SCROLL_IDLE_DELAY = 120;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();
  #lastScrollTop = 0;
  #animationTimeout = null;
  #pointerActivationLockedUntil = 0;
  #scrollIdleTimeout = null;
  #pointerIntentDuringScroll = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    this.#lastScrollTop = document.scrollingElement?.scrollTop ?? 0;

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    this.querySelectorAll('[ref="submenu[]"]').forEach((submenu) => {
      if (!submenu.querySelector('third-level-menu')) return;

      const menuItem = submenu.parentElement?.querySelector(':scope > [ref="menuitem"]');
      if (!(menuItem instanceof HTMLElement)) return;

      menuItem.addEventListener('pointerleave', () => this.#debouncedDeactivate(menuItem), {
        signal: this.#abortController.signal,
      });
      submenu.addEventListener(
        'pointerenter',
        () => {
          this.#debouncedDeactivate.cancel();
        },
        { signal: this.#abortController.signal },
      );
    });

    document.addEventListener('scroll', this.#handleWindowScroll, {
      passive: true,
      signal: this.#abortController.signal,
    });
    this.addEventListener('pointermove', this.#handlePointerMove, { signal: this.#abortController.signal });
    this.addEventListener('pointerleave', this.#handlePointerLeave, { signal: this.#abortController.signal });
    this.addEventListener('click', this.#handleNavigationClick, { signal: this.#abortController.signal });
    window.addEventListener('pageshow', this.#resetNavigationState, { signal: this.#abortController.signal });

    this.#resetNavigationState();

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#scrollIdleTimeout !== null) clearTimeout(this.#scrollIdleTimeout);
    this.#clearAnimationState();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (event.type === 'pointerenter' && performance.now() < this.#pointerActivationLockedUntil) return;

    this.removeAttribute('data-scroll-navigation-only');
    this.#clearAnimationState();
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#clearAnimationState();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      this.#startClosingAnimation(previouslyActiveItem);
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const thirdLevelMenu = submenu?.querySelector('third-level-menu');
    const activateDefault = /** @type {{ activateDefault?: () => void } | null} */ (thirdLevelMenu)?.activateDefault;
    if (typeof activateDefault === 'function') {
      activateDefault.call(thirdLevelMenu);
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {boolean} [force] - Ignore hover guards when scroll must close the menu.
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;
    if (!force && this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    this.#startClosingAnimation(item);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  #handleWindowScroll = () => {
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    this.#lastScrollTop = scrollTop;

    if (this.#scrollIdleTimeout !== null) clearTimeout(this.#scrollIdleTimeout);
    this.#scrollIdleTimeout = setTimeout(() => {
      this.#scrollIdleTimeout = null;
      this.#pointerIntentDuringScroll = false;
    }, SCROLL_IDLE_DELAY);

    if (!isScrollingUp) {
      this.#pointerIntentDuringScroll = false;
      return;
    }

    // After the user deliberately moves onto a menu item, residual wheel or
    // trackpad events from the same upward scroll must not close it again.
    if (this.#pointerIntentDuringScroll && this.matches(':hover')) return;

    this.#pointerActivationLockedUntil = performance.now() + SCROLL_POINTER_GUARD_DELAY;
    this.#resetNavigationState();
  };

  #handlePointerMove = (event) => {
    if (event.pointerType !== 'mouse') return;
    if (event.movementX === 0 && event.movementY === 0) return;
    if (!this.hasAttribute('data-scroll-navigation-only') && performance.now() >= this.#pointerActivationLockedUntil) {
      return;
    }
    if (!(event.target instanceof Element) || !findMenuItem(event.target)) return;

    // A real pointer move is explicit user intent. Release the scroll guard and
    // activate the hovered item immediately instead of waiting for its timeout.
    this.#pointerIntentDuringScroll = this.#scrollIdleTimeout !== null;
    this.#pointerActivationLockedUntil = 0;
    this.activate(event);
  };

  #handlePointerLeave = () => {
    this.#pointerIntentDuringScroll = false;
  };

  #handleNavigationClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a[href]');
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;

    // Let the anchor's native navigation run before changing focus or menu state.
    // Synchronous cleanup can cancel top-level links inside the theme editor iframe.
    setTimeout(() => {
      if (!this.isConnected) return;

      link.blur();
      this.#resetNavigationState();
    }, 0);
  };

  #resetNavigationState = () => {
    this.setAttribute('data-scroll-navigation-only', '');
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();
    this.#clearAnimationState();

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    this.ariaExpanded = 'false';
    this.#state.activeItem = null;

    this.querySelectorAll('[ref="menuitem"][aria-expanded="true"]').forEach((item) => {
      item.setAttribute('aria-expanded', 'false');
    });
    this.#resetThirdLevelPanels();
  };

  #resetThirdLevelPanels = (root = this) => {
    root.querySelectorAll('third-level-menu').forEach((menu) => {
      const reset = /** @type {{ reset?: () => void }} */ (menu).reset;
      if (typeof reset === 'function') {
        reset.call(menu);
        return;
      }

      menu.querySelectorAll('[data-third-level-category]').forEach((item) => {
        item.classList.remove('mega-menu--third-level__category--active');
        if (item.hasAttribute('data-third-level-trigger')) item.setAttribute('aria-expanded', 'false');
      });
      menu.querySelectorAll('[data-third-level-panel]').forEach((panel) => {
        panel.hidden = true;
      });
    });
  };

  #startClosingAnimation = (item) => {
    this.#clearAnimationState();
    item.setAttribute('data-animating', '');
    this.#animationTimeout = setTimeout(() => {
      item.removeAttribute('data-animating');

      const submenu = findSubmenu(item);
      if (submenu) this.#resetThirdLevelPanels(submenu);

      this.#animationTimeout = null;
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  #clearAnimationState = () => {
    if (this.#animationTimeout !== null) {
      clearTimeout(this.#animationTimeout);
      this.#animationTimeout = null;
    }

    this.querySelectorAll('[ref="menuitem"][data-animating]').forEach((item) => {
      item.removeAttribute('data-animating');

      const submenu = findSubmenu(item);
      if (submenu) this.#resetThirdLevelPanels(submenu);
    });
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => {
      if (image.closest('[data-third-level-panel][hidden]')) return;
      image.removeAttribute('loading');
    });
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Controls the product panel shown for each second-level menu link.
 * The links remain navigable; pointer hover and keyboard focus only change
 * the product preview displayed beside them.
 */
class ThirdLevelMenu extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    const signal = this.#abortController.signal;

    this.querySelectorAll('[data-third-level-category]').forEach((trigger) => {
      trigger.addEventListener('pointerenter', () => this.#activate(trigger), { signal });
      trigger.addEventListener('focus', () => this.#activate(trigger), { signal });
    });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  reset() {
    this.querySelectorAll('[data-third-level-category]').forEach((item) => {
      item.classList.remove('mega-menu--third-level__category--active');
      if (item.hasAttribute('data-third-level-trigger')) {
        item.setAttribute('aria-expanded', 'false');
      }
    });

    this.querySelectorAll('[data-third-level-panel]').forEach((panel) => {
      panel.hidden = true;
    });
  }

  activateDefault() {
    const firstTrigger = this.querySelector('[data-third-level-trigger]');

    if (firstTrigger instanceof Element) {
      this.#activate(firstTrigger);
    }
  }

  /**
   * @param {Element} trigger
   */
  #activate(trigger) {
    const controlledPanelId = trigger.getAttribute('aria-controls');

    const triggers = this.querySelectorAll('[data-third-level-category]');
    const panels = this.querySelectorAll('[data-third-level-panel]');

    triggers.forEach((item) => {
      const isActive = item === trigger && controlledPanelId !== null;
      item.classList.toggle('mega-menu--third-level__category--active', isActive);
      if (item.hasAttribute('data-third-level-trigger')) {
        item.setAttribute('aria-expanded', isActive.toString());
      }
    });

    panels.forEach((panel) => {
      panel.hidden = panel.id !== controlledPanelId;
    });

    if (!controlledPanelId) return;
  }
}

if (!customElements.get('third-level-menu')) {
  customElements.define('third-level-menu', ThirdLevelMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const itemContainer = element.matches('.menu-list__list-item')
    ? element
    : element.closest('.menu-list__list-item');

  if (!(itemContainer instanceof HTMLElement)) return null;

  if (itemContainer.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(itemContainer.parentElement?.querySelector('[slot="overflow"]'));
  }

  return itemContainer.querySelector(':scope > [ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
